/**
 * Regroupement local des entités DR en groupes de détection — déterministe,
 * hors ligne, instantané. Première étape de l'analyse du plan : la passe IA
 * (back) n'arbitre ensuite que les cas limites et rédige les libellés.
 *
 * Le grain visé est l'OBSERVATION terrain (ce que l'inspecteur a relevé à UN
 * endroit), pas la constatation de rapport : deux foyers du même désordre à
 * deux endroits distincts = deux observations, même tracés à deux minutes
 * d'écart.
 *
 * Algorithme, par partition (une vignette, ou la feuille entière) :
 * 1. tri des entités par rôle (tracés / textes / marqueurs photo) ;
 * 2. liaison des tracés deux à deux — écart spatial ADAPTATIF à la taille des
 *    tracés, amorti quand le même désordre est redessiné longtemps après,
 *    élargi entre désordres compatibles tracés d'affilée — puis fusion
 *    single-linkage par écart croissant, bornée par MAX_GROUP_SPAN_RATIO ;
 * 3. rattachement de chaque texte au groupe le plus proche de ses ANCRES
 *    (pointes de leaders quand il y en a, bbox sinon), départage temporel ;
 *    un texte multi-leaders nourrit le contexte de chaque groupe pointé ; un
 *    texte sans leader resté orphelin HORS de la vue est une note hors plan
 *    (`off-plan-notes.ts`) : une ligne = un groupe ;
 * 4. passe PHOTOS (`photo-assignment.ts`) : chaque marqueur est rattaché sur
 *    la géométrie réelle des groupes (visée de la flèche, enveloppe, plus
 *    proche, temps en dernier recours) ou reste autonome ;
 * 5. assemblage : nature DÉRIVÉE des membres (désordre / vue d'ensemble /
 *    commentaire), désordre dominant, mesures (`buildPlanContext`), plage
 *    horaire, confiance ; les photos autonomes forment des groupes
 *    « vue d'ensemble » (une prise de vue = un groupe, sauf marqueurs collés).
 *
 * Seuils : voir `grouping-constants.ts` (le fichier de calibration).
 */

import { bboxDiagonal, bboxGap, bboxUnion } from "./geometry";
import {
  LINK_SIZE_FACTOR,
  NEAR_GAP_FLOOR_RATIO,
  NEAR_GAP_MAX_RATIO,
  SAME_DEFECT_FAR_TIME_MS,
  FAR_TIME_GAP_DAMP,
  TEMPORAL_GAP_MS,
  TEMPORAL_SPATIAL_FACTOR,
  OVERVIEW_CLUSTER_GAP_RATIO,
  MAX_GROUP_SPAN_RATIO,
  ATTACH_TIE_TOLERANCE,
} from "./grouping-constants";
import {
  scoreConfidence,
  TINY_ENTITY_RATIO,
  OVERVIEW_CONFIDENCE,
} from "./confidence";
import { buildPlanContext } from "./plan-context";
import { vignetteIdForEntity } from "./vignettes";
import {
  assignPhotos,
  attachRadius,
  buildPhotoTarget,
  type PhotoDecision,
} from "./photo-assignment";
import { groupOffPlanRows, isOffPlanText } from "./off-plan-notes";
import type {
  AnalysisEntity,
  AnalysisEntityRole,
  AnalysisGroup,
  AnalysisGroupKind,
  AnalysisNoteInfo,
  GroupingInput,
  PhotoAttachment,
  PlanAnalysisResult,
  WorldBbox,
} from "./types";

/**
 * Natures « annotation » — miroir des valeurs d'`EntityType`
 * (`drawing/entities/detection.ts`), non importé : une dépendance runtime vers
 * le moteur rendrait ce module intestable sous jsdom.
 */
const ANNOTATION_KINDS = new Set(["text", "mtext", "mleader"]);

/** Rôle d'une entité dans l'analyse. Un marqueur photo prime sur sa nature bloc. */
export function classifyEntity(
  entity: Pick<AnalysisEntity, "markerLabel" | "kind">,
): AnalysisEntityRole {
  if (entity.markerLabel !== null) return "marker";
  if (ANNOTATION_KINDS.has(entity.kind)) return "annotation";
  return "traced";
}

/**
 * Nature d'un groupe, DÉRIVÉE de ses membres — jamais choisie par l'IA :
 * au moins un tracé → désordre ; des textes seuls → désordre s'ils ont été
 * écrits avec une note de légende active, commentaire sinon ; des photos
 * seules → vue d'ensemble (aucun désordre à portée).
 */
export function deriveGroupKind(members: AnalysisEntity[]): AnalysisGroupKind {
  if (members.some((m) => classifyEntity(m) === "traced")) return "disorder";
  const texts = members.filter((m) => classifyEntity(m) === "annotation");
  if (texts.length > 0) {
    return texts.some((t) => t.defectName) ? "disorder" : "comment";
  }
  return "overview";
}

/**
 * Id stable d'un groupe : empreinte FNV-1a des `entityUid` triés. Un groupe
 * recomposé à l'identique garde son id (persistance de l'état « ignoré »).
 */
export function stableGroupId(entityUids: string[]): string {
  let hash = 0x811c9dc5;
  for (const uid of [...entityUids].sort()) {
    for (let i = 0; i < uid.length; i++) {
      hash ^= uid.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    // Séparateur : ["ab","c"] et ["a","bc"] ne doivent pas se confondre.
    hash ^= 0x7c;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Écart de liaison adaptatif entre deux tracés : proportionnel à leur taille
 * (cf. grouping-constants.ts) — deux petits foyers distants ne se lient pas,
 * même sur un très grand plan.
 */
function adaptiveLinkGap(
  a: AnalysisEntity,
  b: AnalysisEntity,
  planDiagonal: number,
): number {
  const size = Math.max(
    bboxDiagonal(a.bbox as WorldBbox),
    bboxDiagonal(b.bbox as WorldBbox),
  );
  const floor = NEAR_GAP_FLOOR_RATIO * planDiagonal;
  const cap = NEAR_GAP_MAX_RATIO * planDiagonal;
  return Math.min(cap, Math.max(floor, LINK_SIZE_FACTOR * size));
}

/**
 * Écart spatial max autorisé pour LIER deux tracés, ou null si incompatibles.
 * Même désordre → proximité adaptative, amortie si redessiné bien plus tard ;
 * sinon il faut la proximité temporelle (même geste terrain) ET une
 * compatibilité de légende.
 */
function linkMaxGap(
  a: AnalysisEntity,
  b: AnalysisEntity,
  noteInfo: AnalysisNoteInfo | undefined,
  planDiagonal: number,
): number | null {
  const nearGap = adaptiveLinkGap(a, b, planDiagonal);

  if (a.defectName !== null && a.defectName === b.defectName) {
    // Même désordre repris longtemps après = probablement un autre relevé.
    const farInTime =
      a.createdAt !== null &&
      b.createdAt !== null &&
      Math.abs(a.createdAt - b.createdAt) > SAME_DEFECT_FAR_TIME_MS;
    return farInTime ? nearGap * FAR_TIME_GAP_DAMP : nearGap;
  }

  const temporallyClose =
    a.createdAt !== null &&
    b.createdAt !== null &&
    Math.abs(a.createdAt - b.createdAt) <= TEMPORAL_GAP_MS;
  if (!temporallyClose) return null;

  const categoryA = a.defectName
    ? noteInfo?.get(a.defectName)?.category
    : undefined;
  const categoryB = b.defectName
    ? noteInfo?.get(b.defectName)?.category
    : undefined;
  if (categoryA && categoryB && categoryA === categoryB) {
    return nearGap * TEMPORAL_SPATIAL_FACTOR;
  }

  // Désordre inconnu des deux côtés : le calque est le meilleur indice restant.
  if (a.defectName === null && b.defectName === null && a.layer === b.layer) {
    return nearGap;
  }
  return null;
}

interface ProtoGroup {
  traced: AnalysisEntity[];
  /**
   * Emprise de référence des rayons de rattachement : les tracés seuls, ou
   * les textes d'une note hors plan.
   */
  anchorBbox: WorldBbox;
  attached: AnalysisEntity[];
  /** Textes pointés par un leader secondaire (contexte seulement, pas de stamp). */
  extraTexts: string[];
  /** Décisions de la passe photos pour les marqueurs rattachés ici. */
  photos: PhotoDecision[];
}

/** Distance temporelle d'une entité à la plage horaire des tracés d'un proto-groupe. */
function temporalDistance(entity: AnalysisEntity, proto: ProtoGroup): number {
  if (entity.createdAt === null) return Number.POSITIVE_INFINITY;
  const times = proto.traced
    .map((t) => t.createdAt)
    .filter((t): t is number => t !== null);
  if (times.length === 0) return Number.POSITIVE_INFINITY;
  const min = Math.min(...times);
  const max = Math.max(...times);
  if (entity.createdAt >= min && entity.createdAt <= max) return 0;
  return entity.createdAt < min ? min - entity.createdAt : entity.createdAt - max;
}

/**
 * Ancres de rattachement d'un texte : les pointes de ses leaders quand il y
 * en a (c'est LÀ que le texte pointe — sa bbox peut s'étendre loin de la
 * zone d'intérêt), sa bbox sinon.
 */
function attachmentAnchors(entity: AnalysisEntity): WorldBbox[] {
  if (entity.leaderTips?.length) {
    return entity.leaderTips.map((tip) => ({
      minX: tip.x,
      minY: tip.y,
      maxX: tip.x,
      maxY: tip.y,
    }));
  }
  return [entity.bbox as WorldBbox];
}

/** Options d'assemblage d'un groupe. */
export interface ComposeGroupOptions {
  /** Nature imposée (vue d'ensemble des photos autonomes) ; dérivée des membres sinon. */
  kind?: AnalysisGroupKind;
  /** Textes de contexte supplémentaires (leaders secondaires). */
  extraTexts?: string[];
  /** Rattachements photo (règle, distance, alternatives) à conserver sur le groupe. */
  photoAttachments?: PhotoAttachment[];
  /** Groupe créé à la main depuis une sélection. */
  manual?: boolean;
}

/**
 * Assemble UN groupe à partir de ses membres (tracés + annotations), sans
 * liaison : c'est la brique commune de `groupEntities`, des corrections
 * manuelles (ajout/retrait de sélection) et des fusions proposées par l'IA.
 * Renvoie null si aucun membre n'a de bbox exploitable.
 */
export function composeGroup(
  entities: AnalysisEntity[],
  planDiagonal: number,
  noteInfo?: AnalysisNoteInfo,
  options?: ComposeGroupOptions,
): AnalysisGroup | null {
  const members = entities
    .filter((e) => e.bbox !== null)
    .sort(compareMembers);
  if (members.length === 0) return null;
  const kind = options?.kind ?? deriveGroupKind(members);

  const traced = members.filter((m) => classifyEntity(m) === "traced");
  const annotations = members.filter((m) => classifyEntity(m) === "annotation");
  const markerMembers = members.filter((m) => classifyEntity(m) === "marker");

  const entityUids = members.map((m) => m.entityUid).sort();
  const id = stableGroupId(entityUids);

  // Emprise : tous les membres (le cadre doit couvrir textes et marqueurs).
  let bbox = members[0].bbox as WorldBbox;
  for (const m of members.slice(1)) bbox = bboxUnion(bbox, m.bbox as WorldBbox);

  // Désordre dominant : le plus fréquent parmi les tracés (les textes pour
  // une note hors plan), premier dessiné en cas d'égalité.
  const counts = new Map<string, { count: number; firstAt: number }>();
  for (const t of traced.length > 0 ? traced : annotations) {
    if (t.defectName === null) continue;
    const entry = counts.get(t.defectName);
    const at = t.createdAt ?? Number.POSITIVE_INFINITY;
    if (entry) {
      entry.count++;
      entry.firstAt = Math.min(entry.firstAt, at);
    } else counts.set(t.defectName, { count: 1, firstAt: at });
  }
  let defectName: string | null = null;
  for (const [name, entry] of counts) {
    const current = defectName ? counts.get(defectName) : undefined;
    if (
      !current ||
      entry.count > current.count ||
      (entry.count === current.count && entry.firstAt < current.firstAt)
    ) {
      defectName = name;
    }
  }
  const info = defectName ? noteInfo?.get(defectName) : undefined;

  let context = buildPlanContext(
    new Map([[id, members]]),
    noteInfo ? { noteInfo } : undefined,
  )[id];

  // Textes pointés par un leader secondaire : contexte seulement (l'entité
  // texte est estampée sur UN groupe, mais ses mots éclairent chaque groupe
  // pointé).
  const extraTexts = (options?.extraTexts ?? []).filter(
    (text) => !(context.texts ?? []).includes(text),
  );
  if (extraTexts.length > 0) {
    context = { ...context, texts: [...(context.texts ?? []), ...extraTexts] };
  }

  const times = members
    .map((m) => m.createdAt)
    .filter((t): t is number => t !== null);
  const timeRange =
    times.length > 0
      ? { start: Math.min(...times), end: Math.max(...times) }
      : null;

  // Les groupes vue d'ensemble n'ont pas de tracés : le score « désordre »
  // n'a pas de sens pour eux, on pose une confiance moyenne fixe.
  const confidence =
    kind === "overview"
      ? OVERVIEW_CONFIDENCE
      : scoreConfidence({
          tracedDefects: (traced.length > 0 ? traced : annotations).map(
            (t) => t.defectName,
          ),
          timeRange,
          missingCreatedAt: traced.some((t) => t.createdAt === null),
          hasText: (context.texts?.length ?? 0) > 0,
          markerCount: markerMembers.length,
          singleTinyEntity:
            members.length === 1 &&
            bboxDiagonal(bbox) < TINY_ENTITY_RATIO * planDiagonal,
        });

  const photoAttachments = options?.photoAttachments ?? [];
  return {
    id,
    kind,
    entityUids,
    handles: members.map((m) => m.handle),
    markerHandles: markerMembers.map((m) => m.handle),
    bbox,
    defectName,
    defectLabel: info?.label && info.label !== defectName ? info.label : null,
    category: info?.category ?? null,
    tracedCount: traced.length,
    context,
    texts: context.texts ?? [],
    markerNumbers: context.markerNumbers,
    timeRange,
    confidence,
    ...(photoAttachments.length > 0 ? { photoAttachments } : {}),
    ...(options?.manual ? { manual: true } : {}),
  };
}

/** Tri stable d'affichage : chronologie, puis position, puis handle. */
function compareMembers(a: AnalysisEntity, b: AnalysisEntity): number {
  const ta = a.createdAt ?? Number.POSITIVE_INFINITY;
  const tb = b.createdAt ?? Number.POSITIVE_INFINITY;
  if (ta !== tb) return ta - tb;
  return a.handle.localeCompare(b.handle);
}

/**
 * Regroupe les entités candidates en groupes de détection. Quand des
 * vignettes sont fournies, chaque vignette est un monde à part : partition
 * préalable (frontière dure — jamais de groupe à cheval) et seuils calibrés
 * sur la diagonale de la vignette, pas de la feuille entière.
 */
export function groupEntities(input: GroupingInput): PlanAnalysisResult {
  const { planDiagonal, noteInfo } = input;

  // Défense en profondeur : le scan exclut déjà fond de plan et entités
  // rattachées, on refiltre ici pour que le module reste sûr par lui-même.
  const candidates = input.entities.filter(
    (e) => !e.background && !e.observationId && e.bbox !== null,
  );

  const vignettes = input.vignettes ?? [];
  const partitions: Array<{
    entities: AnalysisEntity[];
    scaleDiagonal: number;
    /** Emprise du fond de plan de la vue — hors de là, un texte est une note hors plan. */
    contentBbox: WorldBbox | null;
    vignette?: { id: string; name: string | null };
  }> = [];
  if (vignettes.length === 0) {
    partitions.push({
      entities: candidates,
      scaleDiagonal: planDiagonal,
      contentBbox: null,
    });
  } else {
    const byVignette = new Map<string, AnalysisEntity[]>();
    for (const entity of candidates) {
      const id =
        vignetteIdForEntity(entity, vignettes, planDiagonal) ?? vignettes[0].id;
      const bucket = byVignette.get(id);
      if (bucket) bucket.push(entity);
      else byVignette.set(id, [entity]);
    }
    for (const v of vignettes) {
      const entities = byVignette.get(v.id);
      if (!entities) continue;
      partitions.push({
        entities,
        scaleDiagonal: v.diagonal || planDiagonal,
        contentBbox: v.bbox,
        vignette: { id: v.id, name: v.name },
      });
    }
  }

  const groups: AnalysisGroup[] = [];
  const unassignedTextHandles: string[] = [];
  for (const partition of partitions) {
    const result = groupPartition(
      partition.entities,
      partition.scaleDiagonal,
      noteInfo,
      partition.contentBbox,
    );
    for (const group of result.groups) {
      groups.push(
        partition.vignette
          ? {
              ...group,
              vignetteId: partition.vignette.id,
              vignetteName: partition.vignette.name,
            }
          : group,
      );
    }
    unassignedTextHandles.push(...result.unassignedTextHandles);
  }

  // Ordre de parcours : chronologie du relevé, puis position, puis id.
  sortAnalysisGroups(groups);

  return { groups, unassignedTextHandles, unassignedMarkerHandles: [] };
}

/** Rattachement photo tel qu'exposé sur le groupe (alternatives en ids de groupes). */
function toPhotoAttachment(
  decision: PhotoDecision,
  own: number,
  protoIds: string[],
): PhotoAttachment {
  const chosen = decision.candidates.find((c) => c.target === own);
  return {
    handle: decision.marker.handle,
    markerNumber: decision.marker.markerNumber,
    rule: decision.rule,
    distance: decision.distance,
    pointed: chosen?.pointed ?? false,
    inside: chosen?.inside ?? false,
    deltaT: chosen?.deltaT ?? null,
    alternatives: decision.candidates
      .filter((c) => c.target !== own)
      .map((c) => ({
        groupId: protoIds[c.target],
        distance: c.distance,
        pointed: c.pointed,
        inside: c.inside,
        deltaT: c.deltaT,
        photoCount: c.markerCount,
      })),
  };
}

/**
 * Cœur du regroupement sur UNE partition (une vignette, ou la feuille
 * entière) : liaison des tracés, rattachement des textes, notes hors plan,
 * passe photos, assemblage. `scaleDiagonal` est la référence de TOUS les
 * seuils relatifs.
 */
function groupPartition(
  candidates: AnalysisEntity[],
  scaleDiagonal: number,
  noteInfo: AnalysisNoteInfo | undefined,
  contentBbox: WorldBbox | null,
): PlanAnalysisResult {
  const scale = scaleDiagonal;
  const traced = candidates.filter((e) => classifyEntity(e) === "traced");
  const annotations = candidates.filter(
    (e) => classifyEntity(e) === "annotation",
  );
  const markers = candidates.filter((e) => classifyEntity(e) === "marker");
  const maxSpan = MAX_GROUP_SPAN_RATIO * scale;

  // ── Fusion single-linkage des tracés ──────────────────────────────────────
  const parent = traced.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  // Bbox courante de chaque racine, maintenue au fil des fusions pour le
  // garde-fou d'étendue.
  const rootBbox: WorldBbox[] = traced.map((t) => t.bbox as WorldBbox);

  const links: Array<{ i: number; j: number; gap: number }> = [];
  for (let i = 0; i < traced.length; i++) {
    for (let j = i + 1; j < traced.length; j++) {
      const maxGap = linkMaxGap(traced[i], traced[j], noteInfo, scale);
      if (maxGap === null) continue;
      const gap = bboxGap(
        traced[i].bbox as WorldBbox,
        traced[j].bbox as WorldBbox,
      );
      if (gap <= maxGap) links.push({ i, j, gap });
    }
  }
  // Écart croissant : les liaisons les plus sûres d'abord, le garde-fou
  // d'étendue coupe ensuite les chaînes trop gloutonnes.
  links.sort((a, b) => a.gap - b.gap);
  for (const { i, j } of links) {
    const ri = find(i);
    const rj = find(j);
    if (ri === rj) continue;
    const merged = bboxUnion(rootBbox[ri], rootBbox[rj]);
    if (bboxDiagonal(merged) > maxSpan) continue;
    parent[rj] = ri;
    rootBbox[ri] = merged;
  }

  const protosByRoot = new Map<number, ProtoGroup>();
  for (let i = 0; i < traced.length; i++) {
    const root = find(i);
    const proto = protosByRoot.get(root);
    if (proto) proto.traced.push(traced[i]);
    else {
      protosByRoot.set(root, {
        traced: [traced[i]],
        anchorBbox: rootBbox[root],
        attached: [],
        extraTexts: [],
        photos: [],
      });
    }
  }
  const protos = [...protosByRoot.values()];

  // ── Rattachement des textes ───────────────────────────────────────────────
  const unassignedTextHandles: string[] = [];
  const offPlanTexts: AnalysisEntity[] = [];
  const radiusOf = (proto: ProtoGroup): number =>
    attachRadius(bboxDiagonal(proto.anchorBbox), scale);

  for (const entity of annotations) {
    const anchors = attachmentAnchors(entity);
    // Écart à chaque groupe = meilleure ancre (pointe de leader ou bbox).
    const gaps = protos.map((proto) => {
      const gap = Math.min(
        ...anchors.map((anchor) => bboxGap(anchor, proto.anchorBbox)),
        Number.POSITIVE_INFINITY,
      );
      return gap > radiusOf(proto) ? Number.POSITIVE_INFINITY : gap;
    });
    const best = Math.min(...gaps, Number.POSITIVE_INFINITY);
    if (!Number.isFinite(best)) {
      if (contentBbox && isOffPlanText(entity, contentBbox)) {
        offPlanTexts.push(entity);
      } else {
        unassignedTextHandles.push(entity.handle);
      }
      continue;
    }
    // Quasi-égalité spatiale : départage temporel (le texte écrit juste
    // après le tracé).
    const tolerance = best * ATTACH_TIE_TOLERANCE + 1e-12;
    let target = -1;
    let targetTime = Number.POSITIVE_INFINITY;
    for (let p = 0; p < protos.length; p++) {
      if (gaps[p] > best + tolerance) continue;
      const time = temporalDistance(entity, protos[p]);
      if (target === -1 || time < targetTime) {
        target = p;
        targetTime = time;
      }
    }
    protos[target].attached.push(entity);

    // Texte multi-leaders : chaque AUTRE groupe pointé par une de ses pointes
    // reçoit le texte en contexte (l'entité, elle, reste estampée sur un seul).
    const text = entity.text?.trim();
    if (text && anchors.length > 1) {
      for (let p = 0; p < protos.length; p++) {
        if (p === target) continue;
        const pointed = anchors.some(
          (anchor) => bboxGap(anchor, protos[p].anchorBbox) <= radiusOf(protos[p]),
        );
        if (pointed) protos[p].extraTexts.push(text);
      }
    }
  }

  // ── Notes hors plan : une ligne = un groupe ───────────────────────────────
  for (const row of groupOffPlanRows(offPlanTexts)) {
    let anchorBbox = row[0].bbox as WorldBbox;
    for (const text of row.slice(1)) {
      anchorBbox = bboxUnion(anchorBbox, text.bbox as WorldBbox);
    }
    protos.push({
      traced: [],
      anchorBbox,
      attached: row,
      extraTexts: [],
      photos: [],
    });
  }

  // ── Passe photos ──────────────────────────────────────────────────────────
  const targets = protos.map((p) => buildPhotoTarget([...p.traced, ...p.attached]));
  const standalone: PhotoDecision[] = [];
  for (const decision of assignPhotos(markers, targets, scale)) {
    if (decision.target === null) {
      standalone.push(decision);
      continue;
    }
    protos[decision.target].attached.push(decision.marker);
    protos[decision.target].photos.push(decision);
  }

  // ── Assemblage ────────────────────────────────────────────────────────────
  // Les ids sont connus avant composition (empreinte des membres) : les
  // alternatives d'une photo peuvent désigner les groupes voisins.
  const protoIds = protos.map((p) =>
    stableGroupId([...p.traced, ...p.attached].map((e) => e.entityUid)),
  );
  const groups = protos
    .map((proto, index) =>
      composeGroup([...proto.traced, ...proto.attached], scale, noteInfo, {
        extraTexts: proto.extraTexts,
        photoAttachments: proto.photos.map((d) =>
          toPhotoAttachment(d, index, protoIds),
        ),
      }),
    )
    .filter((g): g is AnalysisGroup => g !== null);

  // ── Photos autonomes → vues d'ensemble ────────────────────────────────────
  // Chaque prise de vue est un groupe, sauf marqueurs quasi collés (même
  // zone immédiate) qui n'en forment qu'un.
  const overviewGap = OVERVIEW_CLUSTER_GAP_RATIO * scale;
  if (standalone.length > 0) {
    const ovParent = standalone.map((_, i) => i);
    const ovFind = (i: number): number => {
      while (ovParent[i] !== i) {
        ovParent[i] = ovParent[ovParent[i]];
        i = ovParent[i];
      }
      return i;
    };
    for (let i = 0; i < standalone.length; i++) {
      for (let j = i + 1; j < standalone.length; j++) {
        const gap = bboxGap(
          standalone[i].marker.bbox as WorldBbox,
          standalone[j].marker.bbox as WorldBbox,
        );
        if (gap <= overviewGap) ovParent[ovFind(j)] = ovFind(i);
      }
    }
    const clusters = new Map<number, PhotoDecision[]>();
    for (let i = 0; i < standalone.length; i++) {
      const root = ovFind(i);
      const cluster = clusters.get(root);
      if (cluster) cluster.push(standalone[i]);
      else clusters.set(root, [standalone[i]]);
    }
    for (const cluster of clusters.values()) {
      const group = composeGroup(
        cluster.map((d) => d.marker),
        scale,
        noteInfo,
        {
          kind: "overview",
          photoAttachments: cluster.map((d) => ({
            handle: d.marker.handle,
            markerNumber: d.marker.markerNumber,
            rule: d.rule,
            distance: d.distance,
            pointed: false,
            inside: false,
            deltaT: null,
            alternatives: [],
          })),
        },
      );
      if (group) groups.push(group);
    }
  }

  sortAnalysisGroups(groups);
  return { groups, unassignedTextHandles, unassignedMarkerHandles: [] };
}

/**
 * Ordre de parcours standard des groupes : chronologie du relevé, puis
 * position, puis id. Tri EN PLACE — partagé avec le re-découpage IA.
 */
export function sortAnalysisGroups(groups: AnalysisGroup[]): AnalysisGroup[] {
  return groups.sort((a, b) => {
    const ta = a.timeRange?.start ?? Number.POSITIVE_INFINITY;
    const tb = b.timeRange?.start ?? Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    if (a.bbox.minX !== b.bbox.minX) return a.bbox.minX - b.bbox.minX;
    if (a.bbox.minY !== b.bbox.minY) return a.bbox.minY - b.bbox.minY;
    return a.id.localeCompare(b.id);
  });
}
